import { createElement, useMemo, useState } from 'react';
import { Camera, Download, FileText, Loader2, MoreHorizontal, Power, RefreshCcw, RotateCw, ServerCog } from 'lucide-react';
import DeviceService from '../../services/DeviceService';
import { useAuth } from '../../context/AuthContext';
import { COMMAND_PERMISSIONS, COMMAND_RISK, PERMISSIONS } from '../../utils/permissions';

const QUICK_COMMANDS = [
  { kind: 'chrome.reload', label: 'Reload Chrome', icon: RotateCw, confirm: false },
  { kind: 'config.refresh', label: 'Refresh Config', icon: RefreshCcw, confirm: false },
  { kind: 'chrome.screenshot', label: 'Screenshot', icon: Camera, confirm: false },
  { kind: 'kiosk.restart', label: 'Restart Kiosk', icon: ServerCog, confirm: true },
  { kind: 'system.reboot', label: 'Reboot Device', icon: Power, confirm: true },
  {
    kind: 'update.install_now',
    label: 'Update Now',
    icon: Download,
    confirm: 'Check for a new board app or agent and install it now? The board shows its update screen while it installs, instead of waiting for tonight.'
  }
];

/**
 * How long the agent may run a command. Most finish in seconds; an update has
 * to download a release first (the install itself always finishes once it
 * starts, whatever the deadline).
 */
const DEADLINE_MS = {
  'logs.tail': 45000,
  'update.install_now': 600000
};

/**
 * The three command permissions are separately grantable, so the buttons have to
 * carry their risk class on their face. `inspect` in particular is a surveillance
 * primitive — a screenshot of a display in a prayer space, or the device's logs —
 * and it should not look like a page reload.
 */
const RISK_CHIP = {
  benign: 'bg-gray-100 text-gray-600',
  disruptive: 'bg-amber-100 text-amber-700',
  inspect: 'bg-purple-100 text-purple-700'
};

function RiskChip({ permission }) {
  const risk = COMMAND_RISK[permission];
  if (!risk) return null;
  return (
    <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${RISK_CHIP[risk]}`}>
      {risk}
    </span>
  );
}

function CommandLauncher({ deviceId, onIssued, disabled }) {
  const { can, canAny } = useAuth();
  const [submitting, setSubmitting] = useState('');
  const [showLogs, setShowLogs] = useState(false);
  const [lines, setLines] = useState(100);
  const [error, setError] = useState('');

  // The endpoint resolves its @PreAuthorize from the request body, so there is
  // no single permission that gates the row — each kind is checked on its own.
  const allowedCommands = useMemo(
    () => QUICK_COMMANDS.filter((command) => can(COMMAND_PERMISSIONS[command.kind])),
    [can]
  );
  const canTailLogs = can(PERMISSIONS.BOARD_COMMAND_INSPECT);
  const canIssueAny = canAny([
    PERMISSIONS.BOARD_COMMAND_BENIGN,
    PERMISSIONS.BOARD_COMMAND_DISRUPTIVE,
    PERMISSIONS.BOARD_COMMAND_INSPECT
  ]);

  const issue = async (kind, payload = {}, confirmCommand = false) => {
    const question = typeof confirmCommand === 'string' ? confirmCommand : `Issue ${kind} to this device?`;
    if (confirmCommand && !confirm(question)) return;

    try {
      setError('');
      setSubmitting(kind);
      const issued = await DeviceService.issueCommand(deviceId, {
        kind,
        payload,
        deadlineMs: DEADLINE_MS[kind] ?? 30000
      });
      onIssued?.(issued);
      setShowLogs(false);
    } catch (err) {
      setError(err.message || 'Failed to issue command');
    } finally {
      setSubmitting('');
    }
  };

  if (!canIssueAny) return null;

  return (
    <div className="space-y-4">
      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <div className="flex flex-wrap gap-2">
        {allowedCommands.map((command) => (
          <button
            key={command.kind}
            type="button"
            disabled={disabled || Boolean(submitting)}
            onClick={() => issue(command.kind, {}, command.confirm)}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting === command.kind ? <Loader2 className="h-4 w-4 animate-spin" /> : createElement(command.icon, { className: 'h-4 w-4' })}
            {command.label}
            <RiskChip permission={COMMAND_PERMISSIONS[command.kind]} />
          </button>
        ))}

        {canTailLogs && (
          <button
            type="button"
            disabled={disabled || Boolean(submitting)}
            onClick={() => setShowLogs(true)}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <FileText className="h-4 w-4" />
            Tail Logs
            <RiskChip permission={PERMISSIONS.BOARD_COMMAND_INSPECT} />
            <MoreHorizontal className="h-4 w-4" />
          </button>
        )}
      </div>

      {showLogs && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">Tail device logs</h3>
            <p className="mt-1 text-sm text-gray-500">Request the latest journal lines from the agent. The backend clamps this between 1 and 500.</p>
            <label className="mt-5 block text-sm font-medium text-gray-700">
              Lines
              <input
                type="number"
                min="1"
                max="500"
                value={lines}
                onChange={(event) => setLines(Number(event.target.value))}
                className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
              />
            </label>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowLogs(false)}
                className="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => issue('logs.tail', { lines: Math.min(500, Math.max(1, lines || 100)) })}
                disabled={Boolean(submitting)}
                className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {submitting === 'logs.tail' && <Loader2 className="h-4 w-4 animate-spin" />}
                Issue Command
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CommandLauncher;
